import React, { useEffect, useState } from 'react';
import { authedFetch } from '../../services/admin.service';

interface FeatureConfigField {
  key: string;
  label: string;
  type: 'number' | 'text' | 'boolean';
  description?: string;
  defaultValue?: string | number | boolean | null;
}

interface Feature {
  id: string;
  key: string;
  name: string;
  description: string;
  config_schema?: FeatureConfigField[];
}

interface Plan {
  id: string;
  tier: string;
  name: string;
  price_try: number;
  type: string;
  features: string[];
  featureConfigs?: Record<string, Record<string, any>>;
  description?: string;
  max_courses?: number | null;
  max_lessons_per_day?: number | null;
  max_ai_messages_per_day?: number | null;
  max_code_evaluations_per_day?: number | null;
  max_games_per_day?: number | null;
  max_projects?: number | null;
  max_quizzes_generated_per_day?: number | null;
  max_quizzes_solved_per_day?: number | null;
  max_notes?: number | null;
  is_active?: boolean;
  show_on_landing?: boolean;
  badge_label?: string;
  display_order?: number;
}

export function AdminPlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [msg, setMsg] = useState<{ text: string; success: boolean } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Modals / Forms States
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [modalTab, setModalTab] = useState<'basic' | 'limits'>('basic');
  const [newPlan, setNewPlan] = useState<Partial<Plan>>({
    tier: '', name: '', price_try: 0, type: 'individual',
    description: '', max_courses: null, max_lessons_per_day: null,
    max_ai_messages_per_day: null, max_code_evaluations_per_day: null,
    max_games_per_day: null, max_projects: null,
    max_quizzes_generated_per_day: null, max_quizzes_solved_per_day: null,
    max_notes: null,
    is_active: true, show_on_landing: true, badge_label: '', display_order: 0
  });

  const [showAddFeature, setShowAddFeature] = useState(false);
  const [newFeature, setNewFeature] = useState({ key: '', name: '', description: '' });

  async function load() {
    setIsLoading(true);
    try {
      const [pd, fd] = await Promise.all([
        authedFetch<{ plans: Plan[] }>('/api/admin/plans'),
        authedFetch<{ features: Feature[] }>('/api/admin/features'),
      ]);
      setPlans(pd.plans ?? []);
      setFeatures(fd.features ?? []);
      
      if (selectedPlan) {
        const updated = pd.plans.find(p => p.id === selectedPlan.id);
        if (updated) setSelectedPlan(updated);
      }
    } catch (err) {
      showMsg('Veriler yüklenirken hata oluştu.', false);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function showMsg(text: string, success = true) {
    setMsg({ text, success });
    setTimeout(() => setMsg(null), 3500);
  }

  async function handleCreatePlan(e: React.FormEvent) {
    e.preventDefault();
    if (!newPlan.tier || !newPlan.name) {
      showMsg('Lütfen zorunlu alanları (Tier Key ve Paket İsmi) doldurun.', false);
      return;
    }
    try {
      await authedFetch('/api/admin/plans', {
        method: 'POST',
        body: JSON.stringify(newPlan),
      });
      showMsg('Yeni plan başarıyla oluşturuldu.');
      setShowAddPlan(false);
      resetNewPlan();
      await load();
    } catch (err: any) {
      showMsg(err.message || 'Plan oluşturulamadı.', false);
    }
  }

  function resetNewPlan() {
    setNewPlan({
      tier: '', name: '', price_try: 0, type: 'individual',
      description: '', max_courses: null, max_lessons_per_day: null,
      max_ai_messages_per_day: null, max_code_evaluations_per_day: null,
      max_games_per_day: null, max_projects: null,
      max_quizzes_generated_per_day: null, max_quizzes_solved_per_day: null,
      max_notes: null,
      is_active: true, show_on_landing: true, badge_label: '', display_order: 0
    });
    setModalTab('basic');
  }

  function applyPreset(type: 'starter' | 'pro' | 'school') {
    if (type === 'starter') {
      setNewPlan({
        tier: 'starter_free',
        name: 'Ücretsiz Başlangıç',
        price_try: 0,
        type: 'individual',
        description: 'Kodlamaya ilk adımı atan minikler için temel erişim paketi.',
        max_courses: 2,
        max_lessons_per_day: 3,
        max_ai_messages_per_day: 10,
        max_code_evaluations_per_day: 5,
        max_games_per_day: 3,
        max_projects: 3,
        max_quizzes_generated_per_day: 2,
        max_quizzes_solved_per_day: 5,
        max_notes: 5,
        is_active: true,
        show_on_landing: true,
        badge_label: 'Ücretsiz',
        display_order: 1
      });
    } else if (type === 'pro') {
      setNewPlan({
        tier: 'pro_monthly',
        name: 'Pro Akademi',
        price_try: 299,
        type: 'individual',
        description: 'Sınırsız AI mentörlük, özel projeler ve tüm kurslara erişim.',
        max_courses: null,
        max_lessons_per_day: null,
        max_ai_messages_per_day: 200,
        max_code_evaluations_per_day: null,
        max_games_per_day: null,
        max_projects: null,
        max_quizzes_generated_per_day: null,
        max_quizzes_solved_per_day: null,
        max_notes: null,
        is_active: true,
        show_on_landing: true,
        badge_label: 'En Popüler',
        display_order: 2
      });
    } else if (type === 'school') {
      setNewPlan({
        tier: 'school_unlimited',
        name: 'Kurumsal Okul Paketi',
        price_try: 1499,
        type: 'school',
        description: 'Okul ve sınıflar için toplu öğrenci takibi ve sınırsız kaynaklar.',
        max_courses: null,
        max_lessons_per_day: null,
        max_ai_messages_per_day: null,
        max_code_evaluations_per_day: null,
        max_games_per_day: null,
        max_projects: null,
        max_quizzes_generated_per_day: null,
        max_quizzes_solved_per_day: null,
        max_notes: null,
        is_active: true,
        show_on_landing: true,
        badge_label: 'Kurumsal',
        display_order: 3
      });
    }
  }

  async function handleUpdatePlan(planId: string, updates: Partial<Plan>) {
    try {
      await authedFetch(`/api/admin/plans/${planId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      showMsg('Plan güncellendi.');
      await load();
    } catch (err: any) {
      showMsg(err.message || 'Plan güncellenemedi.', false);
    }
  }

  async function handleDeletePlan(planId: string) {
    if (!window.confirm('Bu planı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) return;
    try {
      await authedFetch(`/api/admin/plans/${planId}`, {
        method: 'DELETE',
      });
      showMsg('Plan silindi.');
      if (selectedPlan?.id === planId) setSelectedPlan(null);
      await load();
    } catch (err: any) {
      showMsg(err.message || 'Plan silinemedi.', false);
    }
  }

  async function handleCreateFeature(e: React.FormEvent) {
    e.preventDefault();
    try {
      await authedFetch('/api/admin/features', {
        method: 'POST',
        body: JSON.stringify(newFeature),
      });
      showMsg('Yeni özellik tanımlandı.');
      setShowAddFeature(false);
      setNewFeature({ key: '', name: '', description: '' });
      await load();
    } catch (err: any) {
      showMsg(err.message || 'Özellik tanımlanamadı.', false);
    }
  }

  async function toggleFeature(planId: string, featureKey: string, hasIt: boolean) {
    try {
      await authedFetch(`/api/admin/plans/${planId}/features`, {
        method: hasIt ? 'DELETE' : 'POST',
        body: JSON.stringify({ featureKey }),
      });
      showMsg(hasIt ? 'Özellik kaldırıldı.' : 'Özellik plana eklendi.');
      await load();
    } catch (err) {
      showMsg('Güncelleme başarısız.', false);
    }
  }

  async function updateFeatureConfig(featureKey: string, fieldKey: string, value: string | number | boolean) {
    if (!selectedPlan) return;
    try {
      await authedFetch(`/api/admin/plans/${selectedPlan.id}/features/config`, {
        method: 'PATCH',
        body: JSON.stringify({ featureKey, config: { [fieldKey]: value } }),
      });
      showMsg('Feature ayarı kaydedildi.');
      setSelectedPlan(prev => prev ? {
        ...prev,
        featureConfigs: {
          ...prev.featureConfigs,
          [featureKey]: {
            ...prev.featureConfigs?.[featureKey],
            [fieldKey]: value,
          },
        },
      } : prev);
    } catch (err) {
      showMsg('Feature ayarı kaydedilemedi.', false);
    }
  }

  function getConfigFields(feature: Feature): FeatureConfigField[] {
    if (feature.config_schema && feature.config_schema.length > 0) return feature.config_schema;
    const schema: Record<string, FeatureConfigField[]> = {
      'ai.mentor': [
        { key: 'daily_message_limit', label: 'Günlük Mentor Mesaj Limiti', type: 'number', description: 'Bu özellik için günlük maksimum AI mentor mesaj sayısı. Null ise sınırsız.' },
      ],
      'ai.code_review': [
        { key: 'daily_review_limit', label: 'Günlük Kod İnceleme Limiti', type: 'number', description: 'Bu özellik için günlük maksimum kod inceleme sayısı.' },
      ],
      'ai.code_fix': [
        { key: 'daily_fix_limit', label: 'Günlük Kod Düzeltme Limiti', type: 'number', description: 'Bu özellik için günlük maksimum kod düzeltme sayısı.' },
      ],
      'ai.project_generator': [
        { key: 'daily_project_limit', label: 'Günlük Proje Üretim Limiti', type: 'number', description: 'Bu özellik için günlük maksimum proje üretim sayısı.' },
      ],
      'ai.quiz_generator': [
        { key: 'daily_quiz_limit', label: 'Günlük Quiz Üretim Limiti', type: 'number', description: 'Bu özellik için günlük maksimum quiz üretim sayısı.' },
      ],
    };
    return schema[feature.key] ?? [];
  }

  function renderFeatureConfigInputs(feature: Feature) {
    const configFields = getConfigFields(feature);
    if (configFields.length === 0) return null;
    const featureConfig = selectedPlan?.featureConfigs?.[feature.key] ?? {};
    return (
      <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 12, background: 'rgba(247, 244, 236, 0.85)', border: '1px solid var(--paper-3)' }}>
        <div style={{ fontSize: '0.83rem', fontWeight: 700, marginBottom: 8 }}>Özellik Ayarları</div>
        {configFields.map(field => {
          const value = featureConfig[field.key] ?? '';
          return (
            <div key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--ink-2)' }}>
                {field.label}
              </label>
              {field.type === 'number' ? (
                <input
                  type="number"
                  value={value ?? ''}
                  placeholder="Sınırsız"
                  onChange={e => updateFeatureConfig(feature.key, field.key, e.target.value ? Number(e.target.value) : null)}
                  style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--paper-3)', background: '#fff' }}
                />
              ) : field.type === 'boolean' ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={e => updateFeatureConfig(feature.key, field.key, e.target.checked)}
                  />
                  {field.description}
                </label>
              ) : (
                <input
                  type="text"
                  value={value ?? ''}
                  onChange={e => updateFeatureConfig(feature.key, field.key, e.target.value)}
                  style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--paper-3)', background: '#fff' }}
                />
              )}
              {field.description && <div style={{ fontSize: '0.76rem', color: 'var(--ink-muted)' }}>{field.description}</div>}
            </div>
          );
        })}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontWeight: 600, color: 'var(--ink-muted)' }}>Plan ve limit verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <style>{`
        /* ──── Visual Upgrade for Modal & Forms ──── */
        .plan-modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(16, 20, 42, 0.75);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1100;
          padding: 20px;
          animation: modalFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }

        .plan-modal-container {
          background: #FFFFFF;
          border: 1px solid var(--paper-3);
          border-radius: 24px;
          width: 100%;
          max-width: 680px;
          max-height: 92vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 24px 60px -12px rgba(16, 20, 42, 0.35);
          overflow: hidden;
        }

        .plan-modal-header {
          padding: 24px 28px 18px 28px;
          background: var(--ink);
          color: var(--paper);
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .plan-modal-header h2 {
          font-family: var(--f-display);
          font-size: 1.5rem;
          margin: 0 0 4px 0;
          color: var(--paper);
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .plan-modal-header p {
          margin: 0;
          font-size: 0.85rem;
          color: rgba(247, 244, 236, 0.7);
        }

        .modal-close-btn {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: var(--paper);
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 18px;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: rotate(90deg);
        }

        .preset-bar {
          background: #F7F4EC;
          padding: 12px 28px;
          border-bottom: 1px solid var(--paper-3);
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .preset-title {
          font-size: 0.78rem;
          font-weight: 800;
          color: var(--ink-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .preset-chip {
          background: #FFFFFF;
          border: 1px solid var(--paper-3);
          border-radius: 100px;
          padding: 5px 14px;
          font-size: 0.82rem;
          font-weight: 700;
          color: var(--ink);
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .preset-chip:hover {
          border-color: var(--teal);
          background: rgba(23, 199, 154, 0.1);
          color: var(--teal-deep);
          transform: translateY(-1px);
        }

        .modal-tab-nav {
          display: flex;
          border-bottom: 1px solid var(--paper-3);
          background: #FFFFFF;
          padding: 0 28px;
          gap: 20px;
        }

        .modal-tab-btn {
          padding: 14px 4px;
          border: none;
          background: none;
          font-size: 0.92rem;
          font-weight: 700;
          color: var(--ink-muted);
          cursor: pointer;
          position: relative;
          transition: color 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .modal-tab-btn.active {
          color: var(--teal-deep);
        }

        .modal-tab-btn.active::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0; right: 0;
          height: 3px;
          background: var(--teal);
          border-radius: 3px 3px 0 0;
        }

        .plan-modal-body {
          padding: 24px 28px;
          overflow-y: auto;
          flex: 1;
        }

        .plan-modal-footer {
          padding: 16px 28px;
          background: #F7F4EC;
          border-top: 1px solid var(--paper-3);
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        /* ──── Form Elements Style ──── */
        .form-section-card {
          background: rgba(247, 244, 236, 0.4);
          border: 1.5px solid var(--paper-3);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .form-section-header {
          font-size: 0.95rem;
          font-weight: 800;
          color: var(--ink);
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .styled-form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 16px;
        }

        .styled-form-group label {
          font-size: 0.84rem;
          font-weight: 700;
          color: var(--ink-2);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .styled-form-group input,
        .styled-form-group select,
        .styled-form-group textarea {
          width: 100%;
          background: #FFFFFF;
          border: 1.5px solid var(--paper-3);
          border-radius: 12px;
          padding: 11px 14px;
          font-size: 0.92rem;
          font-weight: 600;
          color: var(--ink);
          font-family: var(--f-body);
          outline: none;
          transition: all 0.2s ease;
          box-shadow: 0 2px 6px rgba(0,0,0,0.02);
        }

        .styled-form-group input:focus,
        .styled-form-group select:focus,
        .styled-form-group textarea:focus {
          border-color: var(--teal);
          box-shadow: 0 0 0 4px rgba(23, 199, 154, 0.15);
          background: #FFFFFF;
        }

        .field-hint {
          font-size: 0.76rem;
          color: var(--ink-muted);
          font-weight: 500;
          margin-top: 2px;
        }

        .unlimited-badge-btn {
          background: rgba(23, 199, 154, 0.12);
          border: 1px solid var(--teal);
          color: var(--teal-deep);
          font-size: 0.74rem;
          font-weight: 800;
          padding: 3px 10px;
          border-radius: 100px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .unlimited-badge-btn:hover {
          background: var(--teal);
          color: #052E24;
        }

        .form-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        @media (max-width: 600px) {
          .form-grid-2 { grid-template-columns: 1fr; }
        }

        .feature-item-box {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 16px;
          background: #FFFFFF;
          border: 1.5px solid var(--paper-3);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .feature-item-box:hover {
          border-color: var(--teal);
          transform: translateX(2px);
        }

        .feature-item-box.selected {
          border-color: var(--teal);
          background: rgba(23, 199, 154, 0.05);
        }
      `}</style>

      {/* HEADER */}
      <header className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1>Plan & Limit Yönetimi</h1>
          <p>Paket fiyatlarını, üyelik tiplerini ve AI/Ders limitlerini kolayca yönetin.</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="admin-btn-secondary" onClick={() => setShowAddFeature(true)}>
            + Yeni Özellik Tanımla
          </button>
          <button className="admin-btn-primary" onClick={() => { resetNewPlan(); setShowAddPlan(true); }}>
            + Yeni Plan Ekle
          </button>
        </div>
      </header>

      {/* BİLDİRİM PANOSU */}
      {msg && (
        <div 
          style={{ 
            background: msg.success ? 'rgba(23, 199, 154, 0.12)' : 'rgba(172, 59, 48, 0.12)', 
            border: `1.5px solid ${msg.success ? 'var(--teal)' : 'var(--crimson)'}`, 
            color: msg.success ? 'var(--teal-deep)' : 'var(--crimson)', 
            padding: '14px 20px',
            borderRadius: 14,
            fontWeight: 700,
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <span>{msg.text}</span>
        </div>
      )}

      {/* İKİ KOLONLU LAYOUT */}
      <div className="admin-plans-layout">
        {/* SOL: PAKET LİSTESİ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="parent-section-title" style={{ margin: 0 }}>Mevcut Paketler ({plans.length})</h3>
            <span style={{ fontSize: '0.85rem', color: 'var(--ink-muted)', fontWeight: 600 }}>Düzenlemek için pakete tıklayın</span>
          </div>

          {plans.map(plan => {
            const isSelected = selectedPlan?.id === plan.id;
            return (
              <div 
                key={plan.id} 
                className={`plan-admin-card ${isSelected ? 'active-selection' : ''}`}
                onClick={() => setSelectedPlan(plan)}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <h3 style={{ margin: 0, fontSize: '1.25rem', fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>
                        {plan.name}
                      </h3>
                      {plan.badge_label && (
                        <span style={{
                          background: 'var(--gold-soft)',
                          color: 'var(--gold-2)',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          padding: '3px 10px',
                          borderRadius: 100,
                          border: '1px solid rgba(120, 97, 39, 0.2)'
                        }}>
                          {plan.badge_label}
                        </span>
                      )}
                      <span style={{
                        background: plan.show_on_landing !== false ? 'rgba(23, 199, 154, 0.12)' : 'rgba(16, 20, 42, 0.06)',
                        color: plan.show_on_landing !== false ? 'var(--teal-deep)' : 'var(--ink-muted)',
                        border: `1px solid ${plan.show_on_landing !== false ? 'var(--teal)' : 'var(--paper-3)'}`,
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        padding: '3px 10px',
                        borderRadius: 100
                      }}>
                        {plan.show_on_landing !== false ? 'Landing’de Yayında' : 'Landing’de Gizli'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', marginTop: 4 }}>
                      Tier Anahtarı: <code style={{ background: 'rgba(16, 20, 42, 0.06)', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>{plan.tier}</code>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--teal-deep)', fontFamily: 'var(--f-mono)' }}>
                      ₺{plan.price_try}
                      <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', fontWeight: 600 }}>/ay</span>
                    </div>
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      color: plan.is_active ? 'var(--teal-deep)' : 'var(--crimson)'
                    }}>
                      {plan.is_active ? 'Aktif Satışta' : 'Pasif'}
                    </span>
                  </div>
                </div>

                <p style={{ fontSize: '0.88rem', color: 'var(--ink-muted)', margin: '10px 0 14px 0', lineHeight: 1.5 }}>
                  {plan.description || 'Açıklama girilmemiş.'}
                </p>

                {/* Limit Özet Rozetleri */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '6px 0 12px 0' }}>
                  <span style={{ fontSize: '0.76rem', background: '#F7F4EC', padding: '4px 10px', borderRadius: 8, fontWeight: 700, color: 'var(--ink-2)', border: '1px solid var(--paper-3)' }}>
                    Ders: {plan.max_lessons_per_day ?? 'Sınırsız'}
                  </span>
                  <span style={{ fontSize: '0.76rem', background: '#F7F4EC', padding: '4px 10px', borderRadius: 8, fontWeight: 700, color: 'var(--ink-2)', border: '1px solid var(--paper-3)' }}>
                    AI Mesaj: {plan.max_ai_messages_per_day ?? 'Sınırsız'}
                  </span>
                  <span style={{ fontSize: '0.76rem', background: '#F7F4EC', padding: '4px 10px', borderRadius: 8, fontWeight: 700, color: 'var(--ink-2)', border: '1px solid var(--paper-3)' }}>
                    Kod Analiz: {plan.max_code_evaluations_per_day ?? 'Sınırsız'}
                  </span>
                  <span style={{ fontSize: '0.76rem', background: '#F7F4EC', padding: '4px 10px', borderRadius: 8, fontWeight: 700, color: 'var(--ink-2)', border: '1px solid var(--paper-3)' }}>
                    Oyun: {plan.max_games_per_day ?? 'Sınırsız'}
                  </span>
                  <span style={{ fontSize: '0.76rem', background: '#F7F4EC', padding: '4px 10px', borderRadius: 8, fontWeight: 700, color: 'var(--ink-2)', border: '1px solid var(--paper-3)' }}>
                    Quiz Üretme: {plan.max_quizzes_generated_per_day ?? 'Sınırsız'}
                  </span>
                  <span style={{ fontSize: '0.76rem', background: '#F7F4EC', padding: '4px 10px', borderRadius: 8, fontWeight: 700, color: 'var(--ink-2)', border: '1px solid var(--paper-3)' }}>
                    Quiz Çözme: {plan.max_quizzes_solved_per_day ?? 'Sınırsız'}
                  </span>
                  <span style={{ fontSize: '0.76rem', background: '#F7F4EC', padding: '4px 10px', borderRadius: 8, fontWeight: 700, color: 'var(--ink-2)', border: '1px solid var(--paper-3)' }}>
                    Proje: {plan.max_projects ?? 'Sınırsız'}
                  </span>
                  <span style={{ fontSize: '0.76rem', background: '#F7F4EC', padding: '4px 10px', borderRadius: 8, fontWeight: 700, color: 'var(--ink-2)', border: '1px solid var(--paper-3)' }}>
                    Tür: {plan.type === 'school' ? 'Kurumsal Okul' : 'Bireysel'}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed var(--paper-3)', paddingTop: 12, marginTop: 4, flexWrap: 'wrap', gap: 8 }} onClick={e => e.stopPropagation()}>
                  <button 
                    type="button"
                    className="admin-btn-secondary" 
                    style={{ 
                      padding: '6px 12px', 
                      fontSize: '0.78rem',
                      background: plan.show_on_landing !== false ? 'rgba(23, 199, 154, 0.1)' : 'rgba(16, 20, 42, 0.05)',
                      borderColor: plan.show_on_landing !== false ? 'var(--teal)' : 'var(--paper-3)',
                      color: plan.show_on_landing !== false ? 'var(--teal-deep)' : 'var(--ink-muted)'
                    }}
                    onClick={() => handleUpdatePlan(plan.id, { show_on_landing: plan.show_on_landing === false })}
                  >
                    {plan.show_on_landing !== false ? 'Landing’de Gösteriliyor (Gizle)' : 'Landing’de Göster'}
                  </button>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="admin-btn-secondary" style={{ padding: '6px 14px', fontSize: '0.8rem' }} onClick={() => setSelectedPlan(plan)}>
                      Düzenle
                    </button>
                    <button className="admin-btn-secondary" style={{ padding: '6px 14px', fontSize: '0.8rem', color: 'var(--crimson)', borderColor: 'rgba(172, 59, 48, 0.3)' }} onClick={() => handleDeletePlan(plan.id)}>
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* SAĞ: SEÇİLİ PLAN DÜZENLEME PANENİ */}
        <div>
          {selectedPlan ? (
            <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ borderBottom: '1px solid var(--paper-3)', paddingBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="parent-section-title" style={{ margin: 0 }}>"{selectedPlan.name}" Düzenleniyor</h3>
                  <span style={{ background: 'var(--ink)', color: 'var(--paper)', fontSize: '0.78rem', fontWeight: 700, padding: '4px 12px', borderRadius: 100 }}>
                    ID: {selectedPlan.tier}
                  </span>
                </div>
                <p style={{ color: 'var(--ink-muted)', fontSize: '0.85rem', marginTop: 4 }}>
                  Plan detaylarını güncelleyin veya yetkili özellikleri yönetin.
                </p>
              </div>

              {/* HIZLI DÜZENLEME FORMU */}
              <div className="form-section-card">
                <div className="form-section-header">Temel Tanımlamalar</div>
                
                <div className="form-grid-2">
                  <div className="styled-form-group">
                    <label>Paket İsmi</label>
                    <input 
                      type="text" 
                      value={selectedPlan.name} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { name: e.target.value })}
                    />
                  </div>
                  <div className="styled-form-group">
                    <label>Aylık Fiyat (₺)</label>
                    <input 
                      type="number" 
                      value={selectedPlan.price_try} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { price_try: Number(e.target.value) })}
                    />
                  </div>
                </div>

                <div className="styled-form-group">
                  <label>Açıklama</label>
                  <textarea 
                    rows={2}
                    value={selectedPlan.description || ''} 
                    onChange={e => handleUpdatePlan(selectedPlan.id, { description: e.target.value })}
                  />
                </div>

                <div className="form-grid-2">
                  <div className="styled-form-group">
                    <label>Badge Etiketi</label>
                    <input 
                      type="text" 
                      value={selectedPlan.badge_label || ''} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { badge_label: e.target.value })}
                      placeholder="Örn: En Popüler"
                    />
                  </div>
                  <div className="styled-form-group">
                    <label>Sıralama Önceliği</label>
                    <input 
                      type="number" 
                      value={selectedPlan.display_order || 0} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { display_order: Number(e.target.value) })}
                    />
                  </div>
                </div>

                <div className="styled-form-group" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', padding: '12px 16px', borderRadius: 12, border: '1.5px solid var(--paper-3)', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ink)' }}>Plan Satışta / Aktif</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)' }}>Pasif yapılırsa kullanıcılar bu planı satın alamaz.</div>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={selectedPlan.is_active !== false}
                    onChange={e => handleUpdatePlan(selectedPlan.id, { is_active: e.target.checked })}
                    style={{ width: 22, height: 22, cursor: 'pointer', accentColor: 'var(--teal-deep)' }}
                  />
                </div>

                <div className="styled-form-group" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', padding: '12px 16px', borderRadius: 12, border: '1.5px solid var(--paper-3)' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ink)' }}>Landing (Ana Sayfa)'da Göster</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)' }}>İşaretlenirse bu paket ana sayfadaki fiyatlandırma bölümünde listelenir.</div>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={selectedPlan.show_on_landing !== false}
                    onChange={e => handleUpdatePlan(selectedPlan.id, { show_on_landing: e.target.checked })}
                    style={{ width: 22, height: 22, cursor: 'pointer', accentColor: 'var(--teal-deep)' }}
                  />
                </div>
              </div>

              {/* KULLANIM LİMİTLERİ FORMU */}
              <div className="form-section-card">
                <div className="form-section-header">Günlük ve Genel Limitler</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', marginBottom: 14 }}>
                  Boş bıraktığınız veya temizlediğiniz limitler kullanıcıya <strong>"Sınırsız"</strong> erişim sunar.
                </div>

                <div className="form-grid-2">
                  <div className="styled-form-group">
                    <label>
                      <span>Günlük Ders Limiti</span>
                      <button className="unlimited-badge-btn" onClick={() => handleUpdatePlan(selectedPlan.id, { max_lessons_per_day: null })}>
                        {selectedPlan.max_lessons_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                      </button>
                    </label>
                    <input 
                      type="number" 
                      value={selectedPlan.max_lessons_per_day ?? ''} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { max_lessons_per_day: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Sınırsız"
                    />
                  </div>

                  <div className="styled-form-group">
                    <label>
                      <span>Günlük AI Mesaj Limiti</span>
                      <button className="unlimited-badge-btn" onClick={() => handleUpdatePlan(selectedPlan.id, { max_ai_messages_per_day: null })}>
                        {selectedPlan.max_ai_messages_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                      </button>
                    </label>
                    <input 
                      type="number" 
                      value={selectedPlan.max_ai_messages_per_day ?? ''} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { max_ai_messages_per_day: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Sınırsız"
                    />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="styled-form-group">
                    <label>
                      <span>Günlük Kod Değerlendirme Limiti</span>
                      <button className="unlimited-badge-btn" onClick={() => handleUpdatePlan(selectedPlan.id, { max_code_evaluations_per_day: null })}>
                        {selectedPlan.max_code_evaluations_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                      </button>
                    </label>
                    <input 
                      type="number" 
                      value={selectedPlan.max_code_evaluations_per_day ?? ''} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { max_code_evaluations_per_day: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Sınırsız"
                    />
                  </div>

                  <div className="styled-form-group">
                    <label>
                      <span>Günlük Oyun Oynama Limiti</span>
                      <button className="unlimited-badge-btn" onClick={() => handleUpdatePlan(selectedPlan.id, { max_games_per_day: null })}>
                        {selectedPlan.max_games_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                      </button>
                    </label>
                    <input 
                      type="number" 
                      value={selectedPlan.max_games_per_day ?? ''} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { max_games_per_day: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Sınırsız"
                    />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="styled-form-group">
                    <label>
                      <span>Günlük AI Quiz Üretme Limiti</span>
                      <button className="unlimited-badge-btn" onClick={() => handleUpdatePlan(selectedPlan.id, { max_quizzes_generated_per_day: null })}>
                        {selectedPlan.max_quizzes_generated_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                      </button>
                    </label>
                    <input 
                      type="number" 
                      value={selectedPlan.max_quizzes_generated_per_day ?? ''} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { max_quizzes_generated_per_day: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Sınırsız"
                    />
                  </div>

                  <div className="styled-form-group">
                    <label>
                      <span>Günlük Quiz Çözme Limiti</span>
                      <button className="unlimited-badge-btn" onClick={() => handleUpdatePlan(selectedPlan.id, { max_quizzes_solved_per_day: null })}>
                        {selectedPlan.max_quizzes_solved_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                      </button>
                    </label>
                    <input 
                      type="number" 
                      value={selectedPlan.max_quizzes_solved_per_day ?? ''} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { max_quizzes_solved_per_day: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Sınırsız"
                    />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="styled-form-group">
                    <label>
                      <span>Maksimum Kişisel Proje</span>
                      <button className="unlimited-badge-btn" onClick={() => handleUpdatePlan(selectedPlan.id, { max_projects: null })}>
                        {selectedPlan.max_projects === null ? 'Sınırsız' : 'Sınırsız Yap'}
                      </button>
                    </label>
                    <input 
                      type="number" 
                      value={selectedPlan.max_projects ?? ''} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { max_projects: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Sınırsız"
                    />
                  </div>

                  <div className="styled-form-group">
                    <label>
                      <span>Aktif Kurs Limiti</span>
                      <button className="unlimited-badge-btn" onClick={() => handleUpdatePlan(selectedPlan.id, { max_courses: null })}>
                        {selectedPlan.max_courses === null ? 'Sınırsız' : 'Sınırsız Yap'}
                      </button>
                    </label>
                    <input 
                      type="number" 
                      value={selectedPlan.max_courses ?? ''} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { max_courses: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Sınırsız"
                    />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="styled-form-group">
                    <label>
                      <span>Maksimum Kaydedilebilir Not</span>
                      <button className="unlimited-badge-btn" onClick={() => handleUpdatePlan(selectedPlan.id, { max_notes: null })}>
                        {selectedPlan.max_notes === null ? 'Sınırsız' : 'Sınırsız Yap'}
                      </button>
                    </label>
                    <input 
                      type="number" 
                      value={selectedPlan.max_notes ?? ''} 
                      onChange={e => handleUpdatePlan(selectedPlan.id, { max_notes: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Sınırsız"
                    />
                  </div>
                </div>
              </div>

              {/* ÖZELLİK YETKİLERİ */}
              <div className="form-section-card">
                <div className="form-section-header">Özellik Yetkileri ({selectedPlan.features.length})</div>
                <p style={{ color: 'var(--ink-muted)', fontSize: '0.8rem', marginBottom: 14 }}>
                  Bu pakete dahil etmek istediğiniz modül ve özellikleri işaretleyin. Değişiklik anında kaydedilir.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
                  {features.map(f => {
                    const has = selectedPlan.features.includes(f.key);
                    return (
                      <div 
                        key={f.id} 
                        className={`feature-item-box ${has ? 'selected' : ''}`}
                        onClick={() => toggleFeature(selectedPlan.id, f.key, has)}
                      >
                        <input 
                          type="checkbox" 
                          checked={has}
                          onChange={() => {}}
                          style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--teal-deep)', marginTop: 2 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ink)' }}>{f.name}</div>
                          <div style={{ color: 'var(--ink-muted)', fontSize: '0.78rem', marginTop: 2 }}>
                            <code>{f.key}</code> {f.description && `• ${f.description}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="admin-card" style={{ padding: '60px 20px', textAlign: 'center' }}>
              <h3 style={{ fontFamily: 'var(--f-display)', margin: '0 0 8px 0', color: 'var(--ink)' }}>Plan Seçilmedi</h3>
              <p style={{ color: 'var(--ink-muted)', maxWidth: 360, margin: '0 auto', fontSize: '0.9rem' }}>
                Detayları görmek, fiyat ve limitleri düzenlemek veya özellik yetkilerini açıp kapatmak için sol listeden bir plan seçin.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* YENİ PLAN EKLEME MODALI */}
      {showAddPlan && (
        <div className="plan-modal-overlay" onClick={() => setShowAddPlan(false)}>
          <div className="plan-modal-container" onClick={e => e.stopPropagation()}>
            <div className="plan-modal-header">
              <div>
                <h2>Yeni Paket Oluştur</h2>
                <p>Sisteme yeni bir abonelik veya üyelik paketi tanımlayın.</p>
              </div>
              <button className="modal-close-btn" onClick={() => setShowAddPlan(false)}>✕</button>
            </div>

            <div className="preset-bar">
              <span className="preset-title">Hızlı Şablon:</span>
              <button className="preset-chip" type="button" onClick={() => applyPreset('starter')}>
                Ücretsiz Başlangıç
              </button>
              <button className="preset-chip" type="button" onClick={() => applyPreset('pro')}>
                Pro Paket
              </button>
              <button className="preset-chip" type="button" onClick={() => applyPreset('school')}>
                Okul Paketi
              </button>
            </div>

            <div className="modal-tab-nav">
              <button 
                type="button"
                className={`modal-tab-btn ${modalTab === 'basic' ? 'active' : ''}`}
                onClick={() => setModalTab('basic')}
              >
                1. Genel Bilgiler & Fiyat
              </button>
              <button 
                type="button"
                className={`modal-tab-btn ${modalTab === 'limits' ? 'active' : ''}`}
                onClick={() => setModalTab('limits')}
              >
                2. Kullanım Limitleri
              </button>
            </div>

            <form onSubmit={handleCreatePlan} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="plan-modal-body">
                {modalTab === 'basic' ? (
                  <div>
                    <div className="form-grid-2">
                      <div className="styled-form-group">
                        <label>
                          <span>Paket İsmi *</span>
                        </label>
                        <input 
                          type="text" required
                          placeholder="Örn: Pro Akademi"
                          value={newPlan.name}
                          onChange={e => setNewPlan({ ...newPlan, name: e.target.value })}
                        />
                        <span className="field-hint">Kullanıcılara görünecek paket adı.</span>
                      </div>

                      <div className="styled-form-group">
                        <label>
                          <span>Tier Key (Benzersiz Kimlik) *</span>
                        </label>
                        <input 
                          type="text" required
                          placeholder="Örn: pro_monthly"
                          value={newPlan.tier}
                          onChange={e => setNewPlan({ ...newPlan, tier: e.target.value })}
                        />
                        <span className="field-hint">Veritabanı ve kod seviyesindeki anahtar ID.</span>
                      </div>
                    </div>

                    <div className="form-grid-2">
                      <div className="styled-form-group">
                        <label>Aylık Fiyat (₺) *</label>
                        <input 
                          type="number" required min={0}
                          placeholder="0"
                          value={newPlan.price_try}
                          onChange={e => setNewPlan({ ...newPlan, price_try: Number(e.target.value) })}
                        />
                        <span className="field-hint">0 girilirse "Ücretsiz" olarak görünür.</span>
                      </div>

                      <div className="styled-form-group">
                        <label>Plan Türü</label>
                        <select 
                          value={newPlan.type}
                          onChange={e => setNewPlan({ ...newPlan, type: e.target.value })}
                        >
                          <option value="individual">Bireysel (Öğrenci / Veli)</option>
                          <option value="school">Okul / Kurumsal</option>
                        </select>
                        <span className="field-hint">Hedef kitle kategorisi.</span>
                      </div>
                    </div>

                    <div className="styled-form-group">
                      <label>Paket Açıklaması</label>
                      <textarea 
                        rows={2}
                        placeholder="Örn: Sınırsız AI mentörlük, kod editörü ve özel projelere tam erişim sağlayan premium paket."
                        value={newPlan.description}
                        onChange={e => setNewPlan({ ...newPlan, description: e.target.value })}
                      />
                    </div>

                    <div className="form-grid-2">
                      <div className="styled-form-group">
                        <label>Badge Etiketi (Opsiyonel)</label>
                        <input 
                          type="text"
                          placeholder="Örn: En Popüler veya %20 İndirim"
                          value={newPlan.badge_label}
                          onChange={e => setNewPlan({ ...newPlan, badge_label: e.target.value })}
                        />
                      </div>

                      <div className="styled-form-group">
                        <label>Görünüm Sırası</label>
                        <input 
                          type="number"
                          value={newPlan.display_order}
                          onChange={e => setNewPlan({ ...newPlan, display_order: Number(e.target.value) })}
                        />
                        <span className="field-hint">Fiyatlandırma sayfasındaki kart sırası.</span>
                      </div>
                    </div>

                    <div className="styled-form-group" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', background: '#F7F4EC', padding: '12px 16px', borderRadius: 12, border: '1.5px solid var(--paper-3)', marginTop: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--ink)' }}>Landing (Ana Sayfa)'da Göster</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)' }}>İşaretlenirse paket ana sayfada listelenir.</div>
                      </div>
                      <input 
                        type="checkbox" 
                        checked={newPlan.show_on_landing !== false}
                        onChange={e => setNewPlan({ ...newPlan, show_on_landing: e.target.checked })}
                        style={{ width: 20, height: 20, cursor: 'pointer', accentColor: 'var(--teal-deep)' }}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ background: 'rgba(23, 199, 154, 0.08)', padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(23, 199, 154, 0.3)', marginBottom: 16 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--teal-deep)' }}>İpucu: Sınırsız Limitler</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--ink-2)', marginTop: 2 }}>
                        Herhangi bir alanı boş bırakır veya <strong>"Sınırsız Yap"</strong> butonuna basarsanız, kullanıcı o özellikten kısıtlamasız yararlanır.
                      </div>
                    </div>

                    <div className="form-grid-2">
                      <div className="styled-form-group">
                        <label>
                          <span>Günlük Ders Limiti</span>
                          <button type="button" className="unlimited-badge-btn" onClick={() => setNewPlan({ ...newPlan, max_lessons_per_day: null })}>
                            {newPlan.max_lessons_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                          </button>
                        </label>
                        <input 
                          type="number"
                          placeholder="Sınırsız"
                          value={newPlan.max_lessons_per_day ?? ''}
                          onChange={e => setNewPlan({ ...newPlan, max_lessons_per_day: e.target.value ? Number(e.target.value) : null })}
                        />
                        <span className="field-hint">Günde tamamlanabilecek maksimum ders sayısı.</span>
                      </div>

                      <div className="styled-form-group">
                        <label>
                          <span>Günlük AI Mesaj Limiti</span>
                          <button type="button" className="unlimited-badge-btn" onClick={() => setNewPlan({ ...newPlan, max_ai_messages_per_day: null })}>
                            {newPlan.max_ai_messages_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                          </button>
                        </label>
                        <input 
                          type="number"
                          placeholder="Sınırsız"
                          value={newPlan.max_ai_messages_per_day ?? ''}
                          onChange={e => setNewPlan({ ...newPlan, max_ai_messages_per_day: e.target.value ? Number(e.target.value) : null })}
                        />
                        <span className="field-hint">AI Mentör ile sohbet mesaj hakkı.</span>
                      </div>
                    </div>

                    <div className="form-grid-2">
                      <div className="styled-form-group">
                        <label>
                          <span>Günlük Kod Değerlendirme Limiti</span>
                          <button type="button" className="unlimited-badge-btn" onClick={() => setNewPlan({ ...newPlan, max_code_evaluations_per_day: null })}>
                            {newPlan.max_code_evaluations_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                          </button>
                        </label>
                        <input 
                          type="number"
                          placeholder="Sınırsız"
                          value={newPlan.max_code_evaluations_per_day ?? ''}
                          onChange={e => setNewPlan({ ...newPlan, max_code_evaluations_per_day: e.target.value ? Number(e.target.value) : null })}
                        />
                        <span className="field-hint">Kod analizi ve Jüri puanlaması hakkı.</span>
                      </div>

                      <div className="styled-form-group">
                        <label>
                          <span>Günlük Oyun Oynama Limiti</span>
                          <button type="button" className="unlimited-badge-btn" onClick={() => setNewPlan({ ...newPlan, max_games_per_day: null })}>
                            {newPlan.max_games_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                          </button>
                        </label>
                        <input 
                          type="number"
                          placeholder="Sınırsız"
                          value={newPlan.max_games_per_day ?? ''}
                          onChange={e => setNewPlan({ ...newPlan, max_games_per_day: e.target.value ? Number(e.target.value) : null })}
                        />
                        <span className="field-hint">Eğitsel oyunlar oynama hakkı.</span>
                      </div>
                    </div>

                    <div className="form-grid-2">
                      <div className="styled-form-group">
                        <label>
                          <span>Günlük AI Quiz Üretme Limiti</span>
                          <button type="button" className="unlimited-badge-btn" onClick={() => setNewPlan({ ...newPlan, max_quizzes_generated_per_day: null })}>
                            {newPlan.max_quizzes_generated_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                          </button>
                        </label>
                        <input 
                          type="number"
                          placeholder="Sınırsız"
                          value={newPlan.max_quizzes_generated_per_day ?? ''}
                          onChange={e => setNewPlan({ ...newPlan, max_quizzes_generated_per_day: e.target.value ? Number(e.target.value) : null })}
                        />
                        <span className="field-hint">Özel mini sınav üretme hakkı.</span>
                      </div>

                      <div className="styled-form-group">
                        <label>
                          <span>Günlük Quiz Çözme Limiti</span>
                          <button type="button" className="unlimited-badge-btn" onClick={() => setNewPlan({ ...newPlan, max_quizzes_solved_per_day: null })}>
                            {newPlan.max_quizzes_solved_per_day === null ? 'Sınırsız' : 'Sınırsız Yap'}
                          </button>
                        </label>
                        <input 
                          type="number"
                          placeholder="Sınırsız"
                          value={newPlan.max_quizzes_solved_per_day ?? ''}
                          onChange={e => setNewPlan({ ...newPlan, max_quizzes_solved_per_day: e.target.value ? Number(e.target.value) : null })}
                        />
                        <span className="field-hint">Günde çözülebilecek quiz sayısı.</span>
                      </div>
                    </div>

                    <div className="form-grid-2">
                      <div className="styled-form-group">
                        <label>
                          <span>Maksimum Kişisel Proje</span>
                          <button type="button" className="unlimited-badge-btn" onClick={() => setNewPlan({ ...newPlan, max_projects: null })}>
                            {newPlan.max_projects === null ? 'Sınırsız' : 'Sınırsız Yap'}
                          </button>
                        </label>
                        <input 
                          type="number"
                          placeholder="Sınırsız"
                          value={newPlan.max_projects ?? ''}
                          onChange={e => setNewPlan({ ...newPlan, max_projects: e.target.value ? Number(e.target.value) : null })}
                        />
                        <span className="field-hint">Kullanıcının kaydedebileceği proje sayısı.</span>
                      </div>

                      <div className="styled-form-group">
                        <label>
                          <span>Aktif Kurs Limiti</span>
                          <button type="button" className="unlimited-badge-btn" onClick={() => setNewPlan({ ...newPlan, max_courses: null })}>
                            {newPlan.max_courses === null ? 'Sınırsız' : 'Sınırsız Yap'}
                          </button>
                        </label>
                        <input 
                          type="number"
                          placeholder="Sınırsız"
                          value={newPlan.max_courses ?? ''}
                          onChange={e => setNewPlan({ ...newPlan, max_courses: e.target.value ? Number(e.target.value) : null })}
                        />
                        <span className="field-hint">Aynı anda kayıt olunabilecek kurs limiti.</span>
                      </div>
                    </div>

                    <div className="form-grid-2">
                      <div className="styled-form-group">
                        <label>
                          <span>Maksimum Kaydedilebilir Not</span>
                          <button type="button" className="unlimited-badge-btn" onClick={() => setNewPlan({ ...newPlan, max_notes: null })}>
                            {newPlan.max_notes === null ? 'Sınırsız' : 'Sınırsız Yap'}
                          </button>
                        </label>
                        <input 
                          type="number"
                          placeholder="Sınırsız"
                          value={newPlan.max_notes ?? ''}
                          onChange={e => setNewPlan({ ...newPlan, max_notes: e.target.value ? Number(e.target.value) : null })}
                        />
                        <span className="field-hint">Kullanıcının alabileceği toplam ders notu limiti.</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="plan-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowAddPlan(false)}>
                  Vazgeç
                </button>

                {modalTab === 'basic' ? (
                  <button type="button" className="admin-btn-primary" onClick={() => setModalTab('limits')}>
                    Limitlere Geç
                  </button>
                ) : (
                  <button type="submit" className="admin-btn-primary">
                    Paketi Oluştur ve Kaydet
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* YENİ ÖZELLİK TANIMLAMA MODALI */}
      {showAddFeature && (
        <div className="plan-modal-overlay" onClick={() => setShowAddFeature(false)}>
          <div className="plan-modal-container" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
            <div className="plan-modal-header">
              <div>
                <h2>Yeni Özellik Tanımla</h2>
                <p>Sisteme yeni bir yetki/feature key ekleyin.</p>
              </div>
              <button className="modal-close-btn" onClick={() => setShowAddFeature(false)}>✕</button>
            </div>

            <form onSubmit={handleCreateFeature}>
              <div className="plan-modal-body">
                <div className="styled-form-group">
                  <label>Feature Key *</label>
                  <input 
                    type="text" required
                    placeholder="Örn: ai.project_generator"
                    value={newFeature.key}
                    onChange={e => setNewFeature({ ...newFeature, key: e.target.value })}
                  />
                  <span className="field-hint">Kod tarafında <code>useFeatures()</code> ile kontrol edilecek anahtar.</span>
                </div>

                <div className="styled-form-group">
                  <label>Özellik Adı *</label>
                  <input 
                    type="text" required
                    placeholder="Örn: Yapay Zeka ile Proje Üreteci"
                    value={newFeature.name}
                    onChange={e => setNewFeature({ ...newFeature, name: e.target.value })}
                  />
                </div>

                <div className="styled-form-group">
                  <label>Açıklama</label>
                  <textarea 
                    rows={3}
                    placeholder="Örn: Kullanıcının profil ilgi alanlarına göre özel Python projesi oluşturan modül."
                    value={newFeature.description}
                    onChange={e => setNewFeature({ ...newFeature, description: e.target.value })}
                  />
                </div>
              </div>

              <div className="plan-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowAddFeature(false)}>
                  İptal
                </button>
                <button type="submit" className="admin-btn-primary">
                  Özelliği Tanımla
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
